import { useState } from "react";
import { FiX } from "react-icons/fi";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";

export default function CreateUserModal({
  open,
  onClose,
  roles,
  onCreated,
}) {
  const { accessToken } = useAuth();

  const [form, setForm] = useState({
    email: "",
    phone: "",
    password: "",
    roleCodes: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const toggleRole = (roleCode) => {
    setForm((previous) => ({
      ...previous,
      roleCodes: previous.roleCodes.includes(roleCode)
        ? previous.roleCodes.filter((code) => code !== roleCode)
        : [...previous.roleCodes, roleCode],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.email.trim() && !form.phone.trim()) {
      setError("Enter an email address or mobile number.");
      return;
    }

    if (form.password.length < 8) {
      setError("Password must contain at least 8 characters.");
      return;
    }

    if (form.roleCodes.length === 0) {
      setError("Select at least one role.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const user = await apiRequest("/users", {
        method: "POST",
        accessToken,
        body: {
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          password: form.password,
          roleCodes: form.roleCodes,
        },
      });

      onCreated(user);
      onClose();

      setForm({
        email: "",
        phone: "",
        password: "",
        roleCodes: [],
      });
    } catch (err) {
      setError(err.message || "Unable to create user.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold">Create User</h2>
            <p className="mt-1 text-sm text-gray-500">
              Create an ERP login and assign its role.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 p-6">
            {error && (
              <div className="rounded-xl bg-red-50 p-4 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid gap-5 md:grid-cols-2">
              <Field
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(value) =>
                  setForm((previous) => ({ ...previous, email: value }))
                }
                placeholder="manager@charms.com"
              />

              <Field
                label="Mobile Number"
                value={form.phone}
                onChange={(value) =>
                  setForm((previous) => ({ ...previous, phone: value }))
                }
                placeholder="9876543210"
              />

              <div className="md:col-span-2">
                <Field
                  label="Temporary Password *"
                  type="password"
                  value={form.password}
                  onChange={(value) =>
                    setForm((previous) => ({ ...previous, password: value }))
                  }
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>

            <div>
              <label className="mb-3 block text-sm font-medium">
                User Roles *
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 ${
                      form.roleCodes.includes(role.code)
                        ? "border-primary-700 bg-primary-50"
                        : "border-gray-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.roleCodes.includes(role.code)}
                      onChange={() => toggleRole(role.code)}
                      className="mt-1 accent-primary-700"
                    />

                    <span>
                      <span className="block font-medium text-gray-900">
                        {role.name}
                      </span>

                      <span className="mt-1 block text-xs text-gray-500">
                        {role.code}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <p className="rounded-xl bg-gray-50 p-4 text-xs leading-5 text-gray-500">
              Employee profile details such as full name and employee code are
              managed by HRMS. This screen creates the ERP login account.
            </p>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-5">
            <button
              type="button"
              onClick={onClose}
              className="h-11 rounded-xl border border-gray-200 px-5"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="h-11 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-60"
            >
              {saving ? "Creating…" : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium">{label}</label>

      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className="h-12 w-full rounded-xl border border-gray-200 px-4 outline-none focus:border-primary-700"
      />
    </div>
  );
}